import { parseSafeDate } from '@/utils/dateNormalize';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Trash2, Wallet, Calculator, Pencil, Shield, CheckCircle, UserCircle, FileText } from 'lucide-react';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { fr } from 'date-fns/locale';
import { usePharmacies } from '../context/PharmacyContext';
import ModernDateRangePicker from '../components/ModernDateRangePicker';
import SuperAdminPharmacyFilter from '../components/SuperAdminPharmacyFilter';
import ExpensePharmacyFilter from '../components/ExpensePharmacyFilter';
import ExpenseDateRangePicker from '../components/ExpenseDateRangePicker';
import useGranularPermissions from '../hooks/useGranularPermissions';
import CashWizard from '../components/CashWizard';
import { FilterableHeader, useTableFilters } from '../components/TableFilters';
import { toSafeISO } from '../utils/dateNormalize';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : BACKEND_URL.endsWith('/') ? `${BACKEND_URL}api` : `${BACKEND_URL}/api`;

function GestionCaisses() {
  const { getPharmacyIdsParam, selectedPharmacyIds } = usePharmacies();
  const {
    canCreate,
    canUpdate,
    canDelete,
    canViewHistory,
    canViewTotals,
    canFilterPeriod,
    canViewAllUsers,
    canValidateCash
  } = useGranularPermissions();
  const [users, setUsers] = useState([]);
  const [cashClosures, setCashClosures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cashEntryDialogOpen, setCashEntryDialogOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [adminUpdateDialogOpen, setAdminUpdateDialogOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [currentUser] = useState(() => {
    try {
      const stored = sessionStorage.getItem('pharmacy_user') || localStorage.getItem('pharmacy_user');
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error('Failed to parse user from localStorage:', error);
      return null;
    }
  });
  const [superAdminPharmacyFilter, setSuperAdminPharmacyFilter] = useState([]);
  const [dateRange, setDateRange] = useState({
    from: startOfMonth(new Date()),
    to: new Date()
  });

  const [cashFormData, setCashFormData] = useState({
    date: format(new Date(), 'yyyy-MM-dd'),
    user_id: currentUser?.role === 'caissier' ? currentUser.id : '',
    montant_caissier: '',
    notes: '',
  });

  const [adminFormData, setAdminFormData] = useState({
    montant_reel: '',
    notes: '',
  });

  useEffect(() => {
    if (!currentUser?.id) {
      toast.error('Session expirée. Veuillez vous reconnecter.');
      setLoading(false);
      return;
    }
    fetchData();
  }, [selectedPharmacyIds, dateRange, superAdminPharmacyFilter, currentUser]);

  const fetchData = async () => {
    setLoading(true);
    
    if (!currentUser?.id) {
      toast.error('Session invalide. Veuillez vous reconnecter.');
      setLoading(false);
      return;
    }
    
    try {
      const startDate = dateRange?.from || (dateRange?.from === null ? new Date(2000, 0, 1) : startOfMonth(new Date()));
      const endDate = dateRange?.to || (dateRange?.to === null ? new Date(2100, 11, 31) : endOfMonth(new Date()));
      
      const headers = {
        'X-User-Id': currentUser.id,
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      };
      const tsParam = new Date().getTime();
      
      let pharmacyIds;
      if (currentUser?.role === 'super_admin') {
        pharmacyIds = superAdminPharmacyFilter === 'all' ? 'all' : superAdminPharmacyFilter;
      } else {
        pharmacyIds = getPharmacyIdsParam();
      }

      const [usersRes, cashEntriesRes] = await Promise.allSettled([
        axios.get(`${API}/users`, { headers }),
        axios.get(`${API}/cash-closures`, { 
          headers, 
          params: { 
            pharmacy_ids: pharmacyIds,
            start_date: format(startDate, "yyyy-MM-dd'T'00:00:00"),
            end_date: format(endDate, "yyyy-MM-dd'T'23:59:59"),
            _t: tsParam
          } 
        }),
      ]);
      
      // Users : tolérant aux erreurs — fallback sur l'utilisateur courant si /users échoue
      if (usersRes.status === 'fulfilled') {
        const usableUsers = (usersRes.value.data || []).filter(u => u.role !== 'super_admin');
        setUsers(usableUsers.length > 0 ? usableUsers : (currentUser ? [currentUser] : []));
      } else {
        if (usersRes.reason?.response?.status !== 403) console.error('Erreur /users:', usersRes.reason);
        setUsers(currentUser ? [currentUser] : []);
      }
      
      // Cash entries : tolérant aux erreurs
      if (cashEntriesRes.status === 'fulfilled') {
        setCashClosures(cashEntriesRes.value.data);
      } else {
        if (cashEntriesRes.reason?.response?.status !== 403) console.error('Erreur /cash-closures:', cashEntriesRes.reason);
        setCashClosures([]);
      }
    } catch (error) {
      console.error('Erreur de chargement:', error);
      if (!currentUser?.id) {
        toast.error('Session invalide. Veuillez vous reconnecter.');
      } else if (error.response?.status === 401 || error.response?.status === 403) {
        console.warn('Accès refusé. Vérifiez vos permissions.');
      } else {
        toast.error('Erreur lors du chargement des données');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCashEntrySubmit = async (e) => {
    e.preventDefault();
    try {
      const headers = { 'X-User-Id': currentUser?.id || '' };
      
      if (selectedEntry) {
        await axios.put(`${API}/cash-closures/${selectedEntry.id}`, {
          user_id: cashFormData.user_id,
          date: toSafeISO(cashFormData.date),
          montant_caissier: parseFloat(cashFormData.montant_caissier),
          notes: cashFormData.notes
        }, { headers });
        toast.success('Entrée de caisse modifiée avec succès!');
      } else {
        await axios.post(`${API}/cash-closures`, {
          user_id: cashFormData.user_id,
          date: toSafeISO(cashFormData.date),
          montant_caissier: parseFloat(cashFormData.montant_caissier),
          notes: cashFormData.notes
        }, { headers });
        toast.success('Montant de caisse enregistré!');
      }
      
      setCashEntryDialogOpen(false);
      setSelectedEntry(null);
      setCashFormData({
        date: format(new Date(), 'yyyy-MM-dd'),
        user_id: currentUser?.role === 'caissier' ? currentUser.id : '',
        montant_caissier: '',
        notes: '',
      });
      fetchData();
    } catch (error) {
      toast.error(selectedEntry ? "Erreur lors de la modification" : "Erreur lors de l'enregistrement");
    }
  };

  const handleWizardSubmit = async (wizardData) => {
    if (!currentUser?.id) {
      toast.error('Session expirée. Veuillez vous reconnecter.');
      return;
    }
    
    try {
      const headers = { 'X-User-Id': currentUser.id };
      
      const payload = {
        user_id: wizardData.user_id,
        date: toSafeISO(wizardData.date),
        montant_caissier: parseFloat(wizardData.montant_caissier),
        notes: wizardData.notes || '',
      };

      console.log('Envoi du payload:', payload);
      const response = await axios.post(`${API}/cash-closures`, payload, { headers });
      console.log('Réponse du serveur:', response.data);
      
      toast.success('Entrée de caisse enregistrée!');
      
      // Fermer le wizard et recharger les données
      setWizardOpen(false);
      fetchData();
      
    } catch (error) {
      console.error('Erreur lors de l\'ajout:', error);
      if (error.response?.status === 403) {
        console.warn("Accès refusé. Droits administrateur requis.");
      } else if (error.response?.data?.detail) {
        toast.error(`Erreur: ${error.response.data.detail}`);
      } else {
        toast.error("Erreur lors de l'enregistrement de l'entrée");
      }
    }
  };

  const handleWizardUpdate = async (wizardData) => {
    if (!currentUser?.id || !selectedEntry) {
      toast.error('Session expirée ou entrée non sélectionnée.');
      return;
    }
    
    try {
      const headers = { 'X-User-Id': currentUser.id };
      
      const payload = {
        user_id: wizardData.user_id,
        date: toSafeISO(wizardData.date),
        montant_caissier: parseFloat(wizardData.montant_caissier),
        notes: wizardData.notes || '',
      };

      await axios.put(`${API}/cash-closures/${selectedEntry.id}`, payload, { headers });

      // If editing a validated entry and montant_reel was changed, push it via /validate
      const isValidatedEdit = selectedEntry.status === 'validated';
      const newMontantReel = wizardData.montant_reel;
      const hasMontantReelChange = isValidatedEdit
        && newMontantReel !== '' && newMontantReel != null
        && parseFloat(newMontantReel) !== (selectedEntry.montant_reel ?? null);
      if (hasMontantReelChange) {
        await axios.put(`${API}/cash-closures/${selectedEntry.id}/validate`, {
          montant_reel: parseFloat(newMontantReel),
          notes: wizardData.notes || '',
        }, { headers });
      }
      
      toast.success('Entrée de caisse modifiée avec succès!');
      setWizardOpen(false);
      setSelectedEntry(null);
      fetchData();
    } catch (error) {
      console.error('Error in handleWizardUpdate:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la modification');
    }
  };


  const handleAdminUpdate = async (e) => {
    e.preventDefault();
    if (!selectedEntry) return;

    try {
      const headers = { 'X-User-Id': currentUser?.id || '' };
      
      const response = await axios.put(`${API}/cash-closures/${selectedEntry.id}/validate`, {
        montant_reel: parseFloat(adminFormData.montant_reel),
        notes: adminFormData.notes
      }, { headers });
      
      const difference = response.data.difference;
      
      toast.success(
        difference === 0 
          ? 'Caisse équilibrée !' 
          : difference > 0 
          ? `Excédent de ${formatCurrency(difference)}` 
          : `Manque de ${formatCurrency(Math.abs(difference))}`
      );
      
      setAdminUpdateDialogOpen(false);
      setSelectedEntry(null);
      setAdminFormData({ montant_reel: '', notes: '' });
      fetchData();
    } catch (error) {
      if (error.response?.status === 403) {
        console.warn('Accès refusé. Droits administrateur requis.');
      } else {
        toast.error('Erreur lors de la validation');
      }
    }
  };

  const openAdminDialog = (entry) => {
    setSelectedEntry(entry);
    setAdminFormData({
      montant_reel: entry.montant_reel?.toString() || '',
      notes: entry.admin_notes || '',
    });
    setAdminUpdateDialogOpen(true);
  };

  const handleDeleteCashEntry = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette entrée de caisse?')) return;

    try {
      const headers = { 'X-User-Id': currentUser?.id || '' };
      await axios.delete(`${API}/cash-closures/${id}`, { headers });
      toast.success('Entrée de caisse supprimée avec succès');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleEditCashEntry = (entry) => {
    setSelectedEntry(entry);
    setWizardOpen(true); // Ouvrir le wizard au lieu du dialog
  };

  const formatNumberInput = (value) => {
    if (!value) return '';
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    return new Intl.NumberFormat('fr-DZ', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(num);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-DZ', { 
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value) + ' DA';
  };

  // Filtrer les cash entries selon les permissions
  const filteredCashEntries = canViewAllUsers('closures')
    ? cashClosures
    : cashClosures.filter(entry => entry.user_id === currentUser?.id);

  const cashFilterConfig = [
    { key: 'pharmacy_name', accessor: (r) => r.pharmacy_name || '—' },
    { key: 'user', accessor: (r) => r.user_fullname || r.username || '—' },
    { key: 'status', accessor: (r) => r.status === 'validated' ? 'Validée' : r.status === 'anomaly' ? 'Anomalie' : 'En attente' },
  ];
  const { filters: cashFilters, setFilter: setCashFilter, getUniqueValues: cashUniqueValues, filteredData: cashFilteredData } = useTableFilters(filteredCashEntries, cashFilterConfig);
  const displayedCashEntries = cashFilteredData;

  // Calculer les totaux sur les données filtrées
  const totalCashEntries = filteredCashEntries.reduce((sum, e) => sum + (e.montant_caissier || 0), 0);
  const totalReelCash = filteredCashEntries.reduce((sum, e) => sum + (e.montant_reel || 0), 0);
  // Différence : (montant_clôture - montant_théorique) sur les entrées validées
  const totalDifference = filteredCashEntries.reduce(
    (sum, e) => sum + (e.montant_reel != null ? ((e.montant_caissier || 0) - e.montant_reel) : 0),
    0
  );

  // Définir les rôles
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'comptable' || currentUser?.role === 'super_admin';
  const isCaissier = currentUser?.role === 'caissier';

  return (
    <div className="space-y-4 sm:space-y-6 fade-in p-3 sm:p-6 bg-gray-50 min-h-screen" data-testid="gestion-caisses-page">
      {/* En-tête */}
      <div className="flex items-start justify-between mb-4 sm:mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Clôture Caisse</h1>
          <p className="text-gray-500 mt-1 text-sm sm:text-base">Suivi et validation des entrées de caisse</p>
        </div>

      </div>

      {/* Filtres Pharmacie + Dates + Action */}
      <div className="bg-white rounded-2xl sm:rounded-3xl shadow-md p-4 sm:p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex-1 min-w-0">
            {currentUser?.role === 'super_admin' && (
              <ExpensePharmacyFilter
                selectedPharmacies={superAdminPharmacyFilter}
                onChange={setSuperAdminPharmacyFilter}
                currentUser={currentUser}
              />
            )}
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            {canFilterPeriod('closures') && (
              <ExpenseDateRangePicker
                dateRange={dateRange}
                onDateRangeChange={setDateRange}
              />
            )}
            <button
              onClick={() => canCreate('closures') && setWizardOpen(true)}
              disabled={!canCreate('closures')}
              data-testid="add-cash-top-btn"
              className={`flex items-center gap-2 px-8 py-3 rounded-xl text-sm font-semibold transition-all border-2 shadow-sm ${
                canCreate('closures') 
                  ? 'bg-white text-blue-600 border-blue-200 hover:bg-blue-50 hover:border-blue-300' 
                  : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed opacity-60'
              }`}
              title={!canCreate('closures') ? 'Permission refusée' : ''}
            >
              <Plus className="w-5 h-5" />
              {isCaissier ? 'Saisir Mon Montant' : 'Ajouter une entrée'}
            </button>
          </div>
        </div>
      </div>

      {/* KPIs Section */}
      {canViewTotals('closures') ? (
        <div className="grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-4">
        <Card className="stat-card border-l-4 border-l-blue-500">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Montant Clôture Caisse</CardTitle>
            <Wallet className="w-5 h-5 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{formatCurrency(totalCashEntries)}</div>
            <p className="text-xs text-gray-500 mt-1">Total déclaré par les caissiers</p>
          </CardContent>
        </Card>

        <Card className="stat-card border-l-4 border-l-purple-500">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Montant Théorique</CardTitle>
            <Calculator className="w-5 h-5 text-purple-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600">{formatCurrency(totalReelCash)}</div>
            <p className="text-xs text-gray-500 mt-1">Validé par les admins</p>
          </CardContent>
        </Card>

        <Card
          className={`stat-card border-l-4 ${
            totalDifference === 0
              ? 'border-l-green-500'
              : totalDifference > 0
              ? 'border-l-blue-500'
              : 'border-l-red-500'
          }`}
          data-testid="kpi-cash-difference"
        >
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Différence</CardTitle>
            <Calculator
              className={`w-5 h-5 ${
                totalDifference === 0
                  ? 'text-green-500'
                  : totalDifference > 0
                  ? 'text-blue-500'
                  : 'text-red-500'
              }`}
            />
          </CardHeader>
          <CardContent>
            <div
              className={`text-2xl font-bold ${
                totalDifference === 0
                  ? 'text-green-600'
                  : totalDifference > 0
                  ? 'text-blue-600'
                  : 'text-red-600'
              }`}
            >
              {formatCurrency(totalDifference)}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {totalDifference === 0
                ? 'Caisse équilibrée'
                : totalDifference > 0
                ? 'Excédent (Plus)'
                : 'Manque'}
            </p>
          </CardContent>
        </Card>
      </div>
      ) : null}

      {/* Main Card Table */}
      <Card>
        <CardHeader>
          <CardTitle>Entrées de Caisse par Utilisateur</CardTitle>
          <CardDescription>Gestion des montants et validation</CardDescription>
        </CardHeader>
        <CardContent>
          {isCaissier && cashClosures.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <p>Aucune entrée pour cette période</p>
              <p className="text-sm mt-2">Cliquez sur "Saisir Mon Montant" pour enregistrer votre caisse</p>
            </div>
          )}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <FilterableHeader label="Pharmacie" allLabel="TOUS" values={cashUniqueValues['pharmacy_name'] || []} selected={cashFilters.pharmacy_name} onSelect={(v) => setCashFilter('pharmacy_name', v)} className="hidden sm:table-cell" />
                  {!isCaissier && <FilterableHeader label="Caissier" allLabel="TOUS" values={cashUniqueValues['user'] || []} selected={cashFilters.user} onSelect={(v) => setCashFilter('user', v)} className="hidden md:table-cell" />}
                  <TableHead>Mt Caissier de clôture</TableHead>
                  <TableHead className="hidden sm:table-cell">Mt Théorique</TableHead>
                  <TableHead className="hidden md:table-cell">Différence</TableHead>
                  <FilterableHeader label="Statut" allLabel="TOUS" values={cashUniqueValues['status'] || []} selected={cashFilters.status} onSelect={(v) => setCashFilter('status', v)} />
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayedCashEntries.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell className="whitespace-nowrap text-sm">
                      {format(parseSafeDate(entry.date), 'dd/MM/yyyy', { locale: fr })}
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      <Badge className="border bg-gray-50 text-gray-700 border-gray-200">
                        {entry.pharmacy_name || 'N/A'}
                      </Badge>
                    </TableCell>
                    {!isCaissier && (
                      <TableCell className="font-medium hidden md:table-cell">{entry.user_fullname || entry.username || 'Inconnu'}</TableCell>
                    )}
                    <TableCell className="font-bold text-blue-600 whitespace-nowrap text-sm">
                      {formatCurrency(entry.montant_caissier)}
                    </TableCell>
                    <TableCell className="font-bold text-purple-600 hidden sm:table-cell">
                      {entry.montant_reel !== null ? formatCurrency(entry.montant_reel) : '-'}
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {entry.montant_reel != null ? (() => {
                        const diff = (entry.montant_caissier || 0) - entry.montant_reel;
                        return (
                          <Badge 
                            variant="outline"
                            className={
                              diff === 0
                                ? 'bg-green-50 text-green-700 border-green-200'
                                : diff > 0
                                ? 'bg-blue-50 text-blue-700 border-blue-200'
                                : 'bg-red-50 text-red-700 border-red-200'
                            }
                          >
                            {diff === 0 ? '✓ OK' : formatCurrency(diff)}
                          </Badge>
                        );
                      })() : '-'}
                    </TableCell>
                    <TableCell>
                      <Badge variant={entry.status === 'validated' ? 'default' : 'secondary'}>
                        {entry.status === 'validated' ? 'Validé' : 'En attente'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {/* Bouton VALIDER (utilisateurs avec permission, si pending) */}
                        {canValidateCash() && entry.status === 'pending' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openAdminDialog(entry)}
                            className="text-green-600 hover:text-green-700 hover:bg-green-50"
                          >
                            Valider
                          </Button>
                        )}
                        {/* Bouton MODIFIER (disponible avant ET après validation) */}
                        {canUpdate('closures') && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditCashEntry(entry)}
                            data-testid={`edit-cash-entry-${entry.id}`}
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 h-8 w-8 p-0"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                        )}
                        {/* Bouton SUPPRIMER */}
                        {canDelete('closures') && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteCashEntry(entry.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>


      {/* Admin Update Dialog */}
      <Dialog open={adminUpdateDialogOpen} onOpenChange={setAdminUpdateDialogOpen}>
        <DialogContent className="sm:max-w-[425px] p-0 overflow-hidden bg-white/95 backdrop-blur-xl border-white/40 shadow-2xl">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-6 text-white relative overflow-hidden">
            <div className="relative z-10">
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <CheckCircle className="w-6 h-6" />
                Validation Caisse
              </h2>
              <p className="text-blue-100 mt-2 flex items-center gap-2 opacity-90">
                <UserCircle className="w-4 h-4" />
                Caissier: {selectedEntry?.username || 'Admin'}
              </p>
            </div>
            {/* abstract background circles */}
            <div className="absolute -right-4 -top-12 w-32 h-32 bg-white/10 rounded-full blur-2xl"></div>
            <div className="absolute right-12 -bottom-8 w-24 h-24 bg-white/10 rounded-full blur-xl"></div>
          </div>

          <div className="p-6">
            <form onSubmit={handleAdminUpdate} className="space-y-5">
              <div className="p-4 bg-gray-50/50 border border-gray-100 rounded-xl">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-500">Montant Caissier</span>
                  <span className="text-lg font-bold text-gray-900">{formatCurrency(selectedEntry?.montant_caissier)}</span>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="montant_reel" className="text-sm font-semibold text-gray-700">Montant Réel (DA) <span className="text-red-500">*</span></Label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Wallet className="h-5 w-5 text-gray-400" />
                  </div>
                  <Input
                    id="montant_reel"
                    type="number"
                    step="0.01"
                    min="0"
                    value={adminFormData.montant_reel}
                    onChange={(e) => setAdminFormData({ ...adminFormData, montant_reel: e.target.value })}
                    required
                    className="pl-10 h-11 border-gray-200 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all shadow-sm"
                    placeholder="Saisissez le montant réel"
                  />
                </div>
              </div>

              {adminFormData.montant_reel && (
                <div className={`p-4 border rounded-xl transition-all duration-300 ${
                    selectedEntry?.montant_caissier - parseFloat(adminFormData.montant_reel) === 0
                      ? 'bg-emerald-50/50 border-emerald-100'
                      : selectedEntry?.montant_caissier - parseFloat(adminFormData.montant_reel) > 0
                      ? 'bg-blue-50/50 border-blue-100'
                      : 'bg-rose-50/50 border-rose-100'
                  }`}>
                  <p className="text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wider">Différence calculée</p>
                  <div className="flex items-end gap-2">
                    <p className={`text-2xl font-black tracking-tight ${
                      selectedEntry?.montant_caissier - parseFloat(adminFormData.montant_reel) === 0
                        ? 'text-emerald-600'
                        : selectedEntry?.montant_caissier - parseFloat(adminFormData.montant_reel) > 0
                        ? 'text-blue-600'
                        : 'text-rose-600'
                    }`}>
                      {formatCurrency(selectedEntry?.montant_caissier - parseFloat(adminFormData.montant_reel))}
                    </p>
                  </div>
                  <p className={`text-xs font-medium mt-1.5 ${
                      selectedEntry?.montant_caissier - parseFloat(adminFormData.montant_reel) === 0
                        ? 'text-emerald-600'
                        : selectedEntry?.montant_caissier - parseFloat(adminFormData.montant_reel) > 0
                        ? 'text-blue-600'
                        : 'text-rose-600'
                    }`}>
                    {selectedEntry?.montant_caissier - parseFloat(adminFormData.montant_reel) === 0
                      ? '✓ Caisse parfaitement équilibrée'
                      : selectedEntry?.montant_caissier - parseFloat(adminFormData.montant_reel) > 0
                      ? '↑ Excédent (Plus)'
                      : '↓ Manque (Moins)'}
                  </p>
                </div>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="admin_notes" className="text-sm font-semibold text-gray-700">Notes Admin</Label>
                <div className="relative">
                  <div className="absolute top-3 left-3 flex items-center pointer-events-none">
                    <FileText className="h-5 w-5 text-gray-400" />
                  </div>
                  <textarea
                    id="admin_notes"
                    value={adminFormData.notes}
                    onChange={(e) => setAdminFormData({ ...adminFormData, notes: e.target.value })}
                    className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all shadow-sm text-sm min-h-[80px] resize-none"
                    placeholder="Commentaires de validation..."
                  />
                </div>
              </div>

              <div className="pt-2">
                <Button type="submit" className="w-full h-11 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all active:scale-[0.98]">
                  <Calculator className="w-5 h-5 mr-2" />
                  Valider la Caisse
                </Button>
              </div>
            </form>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cash Wizard */}
      <CashWizard
        isOpen={wizardOpen}
        onClose={() => {
          setWizardOpen(false);
          setSelectedEntry(null);
        }}
        onSubmit={selectedEntry ? handleWizardUpdate : handleWizardSubmit}
        users={users}
        currentUser={currentUser}
        editMode={!!selectedEntry}
        initialData={selectedEntry}
      />
    </div>
  );
}

export default GestionCaisses;
